
import React from "react";
import {Action, DashboardContainer, TextLoading} from "../../funcs/utils";
import {Text} from "../../../lib/prm/text";
import {Name} from "../../../lib/prm/name";
import {FlowRender} from "../../../lib/flow-render";
import { sprintf } from "sprintf-js";
import {deep_copy} from "../../../lib/js-utils";
import {calcCategoryAllocation, calcMTWAllocation, calcSecurityAllocation, getLastCategoryAllocation} from "./actions";
import { AllocationClassification } from "../../../lib/proto/security_classification";
import {DashboardTable} from "../dashboard-table";
import {AllocationDoughnut} from "../../../lib/onboarding-shared/widgets/allocation";
import {CREATE_REBALANCE_OP} from "../../../lib/constants";
import {create_csv_blob} from "./csv_util";
import { saveAs } from "file-saver";
import {RadioButton} from "../../../lib/prm/radio-button";

const PORTFOLIO_YEAR = new Text(
  new Name("PORTFOLIO_YEAR", "Total Portfolio Years"),
  "10");
const RISK = new Text(new Name("RISK", "Risk"), "0.5");
const CURRENT_YEAR = new Text(
  new Name("CURRENT_YEAR", "Current Year"), "0");
const PORTFOLIO_AMT = new Text(
  new Name("PORTFOLIO_AMT", "Total Portfolio Value"), "1000.00");
const TDA_ACCOUNT_NUMBER = new Text(
  new Name("TDA_ACCOUNT_NUMBER", "Enter SCHWAB Account Number"));
const CASH_ALLOC_CAP = new Text(
  new Name("CASH_ALLOC_CAP", "Cash Allocation Cap USD"), "0");
const ALLOC_TOGGLE = new RadioButton(
  new Name("ALLOC_TOGGLE"), [ new Name("on") ]);
ALLOC_TOGGLE.do_checkbox = true;

const ALL_PRMS = new FlowRender(0, [
  PORTFOLIO_YEAR,
  RISK,
  CURRENT_YEAR,
  PORTFOLIO_AMT,
  TDA_ACCOUNT_NUMBER,
  CASH_ALLOC_CAP,
  ALLOC_TOGGLE,
]);

const get_risk = onboarding => {
  if (typeof onboarding.risk === "number") {
    return onboarding.risk;
  }
  if ('alloc' in onboarding && typeof onboarding.alloc._risk === "number") {
    return onboarding.alloc._risk;
  }
  return 0.5;
}

class SAAllocator extends React.Component {

  constructor() {
    super();
    const new_state = {
      category_alloc_loading: false,
      security_alloc_loading: false,
      create_rebalance: false,
      csv_downloading: false,
    };

    ALL_PRMS.init(new_state, {}, this);
    CASH_ALLOC_CAP.setDisabled(new_state, true);
    this.state = new_state;
    ALLOC_TOGGLE.setCallback(this.onChange);
  }

  onChange = (component, newState) => {
    CASH_ALLOC_CAP.setDisabled(newState, !ALLOC_TOGGLE.isClicked(newState));
  }

  props_loading = props => {
    return props.onboarding.loading || props.portfolio.loading;
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (this.props_loading(this.props) && !this.props_loading(nextProps)) {
      this.maybeUpdatePrm(nextProps)
        .then(did_update => {
          if (did_update) {
            this.callCalcCategoryAllocation();
          }
        });
    } else if (this.state.category_alloc_loading && !nextState.category_alloc_loading) {
      this.callCalcSecurityAllocation(nextState.category_allocation);
    }
    return true;
  }

  componentDidUpdate = (prevProps) => {
    if (prevProps.notification.loading
      && !this.props.notification.loading
      && this.state.create_rebalance) {
      const rebal_id = this.getRebalanceId();
      const args = {
        id: rebal_id,
        wlf_id: this.props.args.wlf_id,
        username: this.props.args.username,
        acct_index: this.props.args.acct_index,
      }
      this.props.navigatePath("/super-admin/rebalance", args);
    }
  }

  callCalcCategoryAllocation = () => {
    const total_years = parseInt(PORTFOLIO_YEAR.getValue(this.state), 10);
    const risk = parseFloat(RISK.getValue(this.state));
    const time = parseFloat(CURRENT_YEAR.getValue(this.state)) / total_years;
    this.setState({
      category_alloc_loading: true,
    });
    if (this.props.args.MTW) {
      getLastCategoryAllocation(this.props.user.token, this.props.username, this.props.acct_index)
        .then(result => {
          if (result.status === "OK") {
            this.setState({
              category_alloc_loading: false,
              category_allocation: result.payload,
            })
          } else {
            this.setState({
              category_alloc_loading: false,
            });
            console.log("Error", result);
          }
        })
    } else {
      calcCategoryAllocation(this.props.user.token, total_years, risk, time)
        .then(result => {
          this.setState({
            category_alloc_loading: false,
            category_allocation: result,
          })
        })
    }

  }

  callCalcSecurityAllocation = (category_allocation) => {
    if (!category_allocation) {
      category_allocation = this.state.category_allocation;
    }
    const amt_usd = parseFloat(PORTFOLIO_AMT.getValue(this.state));
    let cash_alloc_cap = -1;
    if (!CASH_ALLOC_CAP.getDisabled(this.state)) {
      cash_alloc_cap = parseFloat(CASH_ALLOC_CAP.getValue(this.state));
    }
    if (!category_allocation) {
      return;
    }
    this.setState({
      security_alloc_loading: true,
    })

    if (this.props.args.MTW) {
      calcMTWAllocation(
        this.props.user.token, this.props.username,
        parseInt(this.props.acct_index, 10), cash_alloc_cap)
        .then(result => {
          this.setState({
            security_alloc_loading: false,
            ...result.payload,
          })
        }
      )
    } else {
      const portfolio = this.getPortfolio();
      const current_security_alloc = this.get_security_alloc(portfolio);
      const ignore_sym = this.calc_ignore_sym();
      calcSecurityAllocation(
        this.props.user.token, category_allocation.alloc, amt_usd, current_security_alloc,
        cash_alloc_cap, this.props.username, parseInt(this.props.acct_index, 10),
        ignore_sym)
        .then(result => {
          const non_managed_symbols_in = this.state.non_managed_symbols_in;

          this.setState({
            security_alloc_loading: false,
            ...result,
            non_managed_symbols_in: result.non_managed_symbols_in.map((sym, index) => {
              return {
                sym,
                ignore: non_managed_symbols_in
                  ? non_managed_symbols_in[index].ignore : false,
              }
            })
          })
        })
    }
  }

  calc_ignore_sym = () => {
    if (!this.state.non_managed_symbols_in) {
      return [];
    }
    const syms = [];

    this.state.non_managed_symbols_in.forEach(item => {
      if (item.ignore) {
        syms.push(item.sym);
      }
    });
    return syms;
  }

  getCurrentRisk = () => {
    const portfolio = this.getPortfolio();
    const onboarding = this.getOnboarding();

    if (portfolio) {
      return portfolio.current_risk;
    }
    if (onboarding) {
      return onboarding.risk;
    }
    return 0.;
  }

  maybeUpdatePrm(props) {
    return new Promise(resolve => {
      const portfolio = this.getPortfolio(props);
      const onboarding = this.getOnboarding(props);
      const new_state = deep_copy(this.state);
      let result = false;

      if (portfolio) {
        PORTFOLIO_YEAR.setValue(new_state, sprintf("%d", portfolio.total_years));
        RISK.setValue(new_state, sprintf("%.2f", portfolio.current_risk));
        CURRENT_YEAR.setValue(new_state, sprintf("%.2f", portfolio.current_year_t));
        PORTFOLIO_AMT.setValue(new_state, sprintf("%.2f", portfolio.current_price));
        result = true;
      } else if (onboarding) {
        const risk = get_risk(onboarding);
        PORTFOLIO_YEAR.setValue(new_state, sprintf("%d", onboarding.access_funds_years));
        RISK.setValue(new_state, sprintf("%.2f", risk));
        CURRENT_YEAR.setValue(new_state, "0");
        if ('invest_start_amt' in onboarding) {
          PORTFOLIO_AMT.setValue(new_state, onboarding.invest_start_amt);
        }
        result = true;
      }

      if (result) {
        this.setState(new_state, () => {
          return resolve(result);
        })
      } else {
        return resolve(result);
      }
    })
  }

  getOnboarding = (props) => {
    if (!props) {
      props = this.props;
    }
    if (!this.hasUserAcct()) {
      return null;
    }

    const username = this.props.username;
    const index = parseInt(this.props.acct_index, 10);
    const index_key = sprintf("%04d", index);
    return props.onboarding[username] && props.onboarding[username][index_key];
  }

  getRebalanceId = () => {
    if (!this.hasUserAcct()) {
      return null;
    }
    const username = this.props.username;
    const index = parseInt(this.props.acct_index, 10);
    const rebal = this.props.notification[username][index].current_notifs[0];
    return rebal.id;
  }

  getPortfolio = (props) => {
    if (!props) {
      props = this.props;
    }
    if (!this.hasUserAcct()) {
      return null;
    }
    const username = props.username;
    const index = parseInt(props.acct_index, 10);
    return props.portfolio[username]
      && props.portfolio[username][index]
      && props.portfolio[username][index].portfolio;
  }

  get_security_alloc = portfolio => {
    if (!portfolio || portfolio.rebalances.length === 0) {
      return {};
    }
    const last_rebal = portfolio.rebalances[portfolio.rebalances.length - 1];
    const security_alloc = {};
    // TODO: last_rebal px is in mils, want usd
    Object.keys(last_rebal).forEach(sym => {
      if (sym[0] !== "_") {
        const price = last_rebal[sym].px || last_rebal[sym].price;
        security_alloc[sym] = {
          size: last_rebal[sym].size,
          sym,
          px: price / 1e4,
        }
      }
    })
    return security_alloc;
  }

  componentDidMount() {
    if (!this.hasUserAcct()) {
      return;
    }

    const username = this.props.username;
    const index = parseInt(this.props.acct_index, 10);
    if (!this.getOnboarding()) {
      this.props.loadOnboardingData(index, username);
    }
    if (!this.getPortfolio()) {
      this.props.loadPortfolio(username, index);
    } else {
      this.maybeUpdatePrm(this.props);
      this.callCalcCategoryAllocation();
    }
    this.props.loadUserQueueItems(username, index, "Rebalance2");
  }

  hasUserAcct = () => {
    const intRe = /^\d+$/;
    return this.props.username && intRe.test(this.props.acct_index);
  }

  renderPrmCb = (prm, index, toggle_prm) => {
    const item_style = {
      display: "flex",
      alignItems: "center",
    }
    const input_style = {
      width: "100%",
      textAlign: "center",
    }

    const toggle_div = toggle_prm
      ? toggle_prm.renderCheckbox(0, this)
      : null;

    return (
      <div style={item_style} key={index}>
        <div style={{ width: "2rem"}}>
          { toggle_div }
        </div>
        <div style={{ flex: "3", fontWeight: "bold"}}>{prm.name.label}</div>
        <div style={{ flex: "2"}}>
          { prm.renderInput(this, input_style )}
        </div>
      </div>
    )
  }

  renderPrm = () => {
    const client = this.props.client;
    const prms = [ PORTFOLIO_YEAR, RISK, CURRENT_YEAR ];
    const container_style = {
      width: "30rem",
      paddingTop: "1rem",
      position: "relative",
    }
    const is_loading = this.props_loading(this.props);
    const is_alloc_loading = this.state.category_alloc_loading
    return (
      <DashboardContainer
        title={"Portfolio Data"}
        spinner={is_loading}
        client={client}>
        <div style={container_style}>
          { prms.map((prm, index) => this.renderPrmCb(prm, index)) }
        </div>
        <div align={"center"}>
          <Action onClick={this.callCalcCategoryAllocation}>
            <TextLoading
              label={"Calculate"}
              loading={is_alloc_loading}/>
          </Action>
        </div>
      </DashboardContainer>
    )
  }

  dumpCategoryTable = alloc => {
    if (!alloc) {
      return (
        <div align={"center"}>
          <span>** EMPTY **</span>
        </div>
      );
    }

    const title_style = {
      fontWeight: "bold",
    }
    const blank_style = {
      width: "1rem",
      height: "1rem",
    };
    const table_index = 'table_index' in alloc ? alloc.table_index : alloc._table_index;
    const data = [
      [ "Table Index", sprintf("%.2f", table_index)],
      [ (<div style={blank_style}/>), (<div style={blank_style}/>) ],
      [(<span style={title_style}>Investment Category</span>),
        (<span style={title_style}>Percent</span>)],
    ];
    let keys;
    if (alloc.alloc) {
      keys = Object.keys(alloc.alloc);
    } else {
      keys = Object.keys(alloc).filter(key => !key.startsWith("_"));
    }
    //const keys = Object.keys(alloc.alloc);
    keys.sort((cat_a, cat_b) => {
      if (alloc.alloc) {
        return alloc.alloc[cat_b] - alloc.alloc[cat_a];
      }
      return alloc[cat_b] - alloc[cat_a];

    })

    keys.forEach(key => {
      const amt = alloc.alloc ? alloc.alloc[key] : alloc[key];
      data.push([
        AllocationClassification[key].label,
        sprintf("%.2f", amt * 100),
      ])
    })

    return (
      <div>
        <div>
          <DashboardTable
            data={data}/>
        </div>
      </div>
    )

  }

  renderGraph = alloc => {
    if (!alloc) {
      return (
        <div align={"center"}>
          <span>** EMPTY **</span>
        </div>
      );
    }

    const container_style = {
      margin: "0",
    }
    return (
      <AllocationDoughnut
        containerStyle={container_style}
        alloc={alloc.alloc || alloc}
        client={this.props.client}/>
    )
  }

  renderCategoryAllocation = () => {
    const client = this.props.client;
    const is_loading = this.state.category_alloc_loading;
    const alloc = this.state.category_allocation;
    const container_style = {
      display: "flex",
      paddingTop: "1rem",
    }
    const is_MTW = this.props.args.MTW;
    const cat_title = is_MTW ? "Category Allocation - MTW" : "Category Allocation";
    return (
      <DashboardContainer
        title={cat_title}
        spinner={is_loading}
        client={client}>
        <div style={container_style}>
          <div style={{width: "50%"}}>
            { this.dumpCategoryTable(alloc) }
          </div>
          <div style={{width: "50%"}}>
            { this.renderGraph(alloc) }
          </div>
        </div>
      </DashboardContainer>
    )
  }

  renderSecurityAllocTable = () => {
    const {
      category_aggregation, security_allocation,
      category_order } = this.state;
    if (!category_aggregation || !security_allocation || !category_order) {
      return (
        <div align={"center"}>
          *** EMPTY ***
        </div>
      )
    }

    let total_usd = 0.;
    const cat_indices = [];

    const cat_style = {
      fontWeight: "bold",
    }
    const data = [
      ["Category",
      "Security",
      "Description",
      "Size",
      "Security Px",
      "Total"],
    ];
    category_order.forEach(cat => {
      const category_label = AllocationClassification[cat].label;
      const cat_amt =
        sprintf("$%.02f", category_aggregation[cat].total_cents / 100);
      total_usd += category_aggregation[cat].total_cents / 100;
      cat_indices.push(data.length);
      const title = [
        (<span style={cat_style}>{category_label}</span>),
        "",
        "",
        "",
        "",
        (<div align={"right"}>{cat_amt}</div>),
      ]
      data.push(title);

      const syms = Object.keys(category_aggregation[cat]).filter(key => key !== "total_cents");
      syms.forEach((sec, index) => {
        const curr = category_aggregation[cat][sec];
        const desc = sec !== "CASH"
          ? security_allocation[sec].description : "";
        const sec_amt = sprintf("$%.02f", curr.price_cents/100.);
        if (index === 0) {
          title[1] = (<div>{sec}</div>);
          title[2] = (<div>{desc}</div>);
          title[3] = sprintf("%d", curr.size);
          title[4] = (<div>{sec_amt}</div>);
        } else {
          data.push([
            "",
            (<div>{sec}</div>),
            (<div>{desc}</div>),
            sprintf("%d", curr.size),
            (<div>{sec_amt}</div>),
            ""
          ]);
        }
      })
    });

    // Dump total
    cat_indices.push(data.length);
    const total_str = sprintf("$%.02f", total_usd);
    data.push([
      (<span style={cat_style}>Total</span>),
      "",
      "",
      "",
      "",
      (<div align={"right"}>{total_str}</div>),
    ])

    const style_rules = [
      {
        pred: row => cat_indices.includes(row),
        style: {
          borderTop: "solid 1px black",
        }
      }
    ]

    return (
      <DashboardTable
        style={{ width: "100%"}}
        tdStyleLut={style_rules}
        data={data}
      />
    )
  }

  renderRebalanceButton = () => {
    if (!this.hasUserAcct()) {
      // Only able to send rebalances for a specific user.
      return null;
    }

    const username = this.props.username;
    const index = parseInt(this.props.acct_index, 10);

    const has_rebal = this.props.notification
      && this.props.notification[username]
      && this.props.notification[username][index]
      && this.props.notification[username][index].current_notifs.length > 0;

    const text = has_rebal ? "Update Rebalance" : "Create Rebalance";
    const op = has_rebal ? CREATE_REBALANCE_OP.UPDATE : CREATE_REBALANCE_OP.CREATE;
    const current_risk = this.getCurrentRisk();
    const new_risk = parseFloat(RISK.getValue(this.state));
    const is_loading = this.props.notification.loading;
    const portfolio = this.getPortfolio();
    const cash_usd = portfolio
      ? portfolio.cash[portfolio.cash.length - 1]
      : 0;

    const portfolio_usd = parseFloat(PORTFOLIO_AMT.getValue(this.state));
    const on_click = () => {
      this.setState({
        create_rebalance: true,
      });
      if (!this.props.irr.scores) {
        this.props.loadIRRScores();
      }
      this.props.createUserRebalance(
        username, index, current_risk, new_risk, op,
        this.state.category_allocation,
        this.state.security_allocation,
        this.state.trade_orders, portfolio_usd, cash_usd);
    }
    return (
      <div align={"center"} style={{marginTop: "2rem"}}>
        <Action onClick={on_click}>
          <TextLoading
            loading={is_loading}
            label={text}/>
        </Action>
      </div>
    )
  }

  downloadCSV = () => {
    const trade_orders = this.state.trade_orders;
    const tda_account_number = TDA_ACCOUNT_NUMBER.getValue(this.state);
    const filename = sprintf("rebalance_%s.csv", tda_account_number);
    const csv_blob = create_csv_blob(trade_orders, tda_account_number);
    saveAs(csv_blob, filename);
  }

  renderCsvSection = () => {
    if (this.hasUserAcct()) {
      return null;
    }

    const container_style = {
      width: "30rem",
      paddingTop: "3rem",
    }

    const tda_empty = TDA_ACCOUNT_NUMBER.isBlank(this.state);
    const has_trade_orders = !!this.state.trade_orders;
    return (
      <div>
        <div style={container_style}>
          { this.renderPrmCb(TDA_ACCOUNT_NUMBER, 0)}
        </div>
        <div align={"center"}>
          <Action
            disabled={tda_empty || !has_trade_orders}
            onClick={this.downloadCSV}>
            <TextLoading
              label={"Download CSV"}
              loading={this.state.csv_downloading}/>
          </Action>
        </div>
      </div>

    )
  }

  maybeRenderIgnoreSyms = () => {
    const non_managed_symbols_in = this.state.non_managed_symbols_in;

    if (!non_managed_symbols_in || non_managed_symbols_in.length === 0) {
      return null;
    }

    const on_click = index => () => {
      const new_val = deep_copy(non_managed_symbols_in);
      new_val[index].ignore = !new_val[index].ignore;
      this.setState({
        non_managed_symbols_in: new_val,
      })
    }

    const rnd_sym = (item, index) => {
      return (
        <div
          key={index}
          style={{ display: "flex", margin: "0.5rem 1rem", alignItems: "center"}}>
          <div style={{ marginRight: "1rem" }}>
            <input type={"checkbox"} checked={item.ignore} onChange={on_click(index)}/>
          </div>
          <div>
            {item.sym}
          </div>
        </div>
      )
    }

    return (
      <div style={{ fontWeight: "bold", marginLeft: "4rem" }}>
        <div>Non managed asset - check to keep</div>
        { non_managed_symbols_in.map(rnd_sym) }
      </div>
    )
  }

  renderSecurityAllocation = () => {
    const client = this.props.client;
    const is_loading = this.state.security_alloc_loading;
    let title = "Security Allocation";
    if (this.props.args.MTW) {
      title += " - MTW";
    }
    return (
      <DashboardContainer
        title={title}
        spinner={is_loading}
        client={client}>
        <div align={"center"}>
          <Action onClick={() => this.callCalcSecurityAllocation()}>
            <TextLoading label={"Calculate"} loading={is_loading}/>
          </Action>
        </div>
        <div style={{display: "flex", paddingTop: "1rem"}}>
          <div style={{ width: "30rem"}}>
            {this.renderPrmCb(PORTFOLIO_AMT, 0)}
            {this.renderPrmCb(CASH_ALLOC_CAP, 1, ALLOC_TOGGLE)}
          </div>
          { this.maybeRenderIgnoreSyms() }
        </div>
        <div>
          {this.renderSecurityAllocTable()}
        </div>
        {this.renderRebalanceButton()}
        {this.renderCsvSection() }
      </DashboardContainer>
    )
  }

  render = () => {

    return (
      <div className={"img-col"} id={"allocator"}>
        { this.renderPrm() }
        { this.renderCategoryAllocation() }
        { this.renderSecurityAllocation() }
      </div>
    )
  }
}

export { SAAllocator };
